/**
 * Date-of-birth validation shared by every login entry point.
 *
 * Ported from the DOB section of the previous portal
 * (`trillium-payment-portal-ui`, `src/Containers/Login/index.jsx` +
 * `src/BaseComponents/Date.jsx`), which got this right: the patient types
 * whatever they like, and the date is checked when they leave the field —
 * never mid-keystroke. Its rules, verbatim:
 *
 *   validator.isDate(formatDate(date)) && date < new Date() && date > mindate
 *   // mindate = new Date('01/01/1871')
 *
 * i.e. a real calendar date, not in the future, not before 1871. The old
 * field also carried `maxDate={new Date()}` so the calendar could not offer
 * a future day at all.
 *
 * The previous implementation here only regex-checked the shape
 * (`^\d{4}-\d{2}-\d{2}$`), so month 99 / day 45 / Feb 30 reached the
 * backend and came back as the generic "PIN or date of birth doesn't match"
 * — no hint that the date itself was the problem.
 */

import { z } from 'zod';

/**
 * Local midnight on the given date.
 *
 * Built with `setFullYear` rather than `new Date(y, m, d)` because that
 * constructor maps years 0-99 onto 1900-1999 — `new Date(1, 0, 1)` is
 * 1901-01-01, which let a typed year of "0001" pass the 1871 floor. Passing
 * all three components to `setFullYear` in one call also avoids the
 * intermediate rollover a separate month/day assignment would cause on
 * Feb 29.
 */
function localMidnight(year: number, month: number, day: number): Date {
  const d = new Date(0);
  d.setFullYear(year, month - 1, day);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Earliest accepted birth date, matching the old portal's
 * `mindate = new Date('01/01/1871')`.
 */
export const DOB_MIN_DATE = localMidnight(1871, 1, 1);

const ISO_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Days in `month` (1-12) of `year`, accounting for leap years. */
export function daysInMonth(month: number, year: number): number {
  if (month < 1 || month > 12) return 31;
  // Day 0 of the following month is the last day of this one.
  return localMidnight(year, month + 1, 0).getDate();
}

/**
 * Validate an ISO `YYYY-MM-DD` date of birth.
 *
 * @returns a patient-facing error message, or `null` when the date is valid.
 */
export function validateIsoDob(iso: string, now: Date = new Date()): string | null {
  if (!iso) return 'Date of birth is required';

  const match = ISO_SHAPE.exec(iso);
  if (!match) return 'Please complete your date of birth';

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // One message for every impossible date, as the old portal did — the
  // patient can see which box is wrong, so naming it adds nothing.
  if (month < 1 || month > 12) return 'Enter a valid date';
  if (day < 1 || day > daysInMonth(month, year)) return 'Enter a valid date';

  // Compare at day granularity so a birthday of today is valid regardless
  // of the current time of day.
  const entered = localMidnight(year, month, day);
  const today = localMidnight(now.getFullYear(), now.getMonth() + 1, now.getDate());
  if (entered.getTime() > today.getTime()) {
    return 'Date of birth cannot be in the future';
  }
  if (entered.getTime() < DOB_MIN_DATE.getTime()) {
    return 'Enter a valid date';
  }

  return null;
}

/** True when `iso` is a real, plausible date of birth. */
export function isValidIsoDob(iso: string, now: Date = new Date()): boolean {
  return validateIsoDob(iso, now) === null;
}

/**
 * Zod schema for the ISO date emitted by `Dob3BoxInput`.
 *
 * Exported so every login entry point (PIN+DOB and magic-link) enforces the
 * same rules — they previously carried two slightly different shape-only
 * regexes and neither rejected an impossible date.
 */
export const dobIsoSchema = z.string().superRefine((iso, ctx) => {
  const message = validateIsoDob(iso);
  if (message) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  }
});
